/**
 * Require variant selection (Le Pantalon).
 * Lands the PDP with NO size preselected and the Add-to-Cart button gated until a
 * size is chosen. Gating is opt-out via the `require_variant_selection` theme setting,
 * and only emitted by main-product.liquid for single-option, multi-value products
 * (single-variant and multi-option products are never gated).
 *
 * The flash-proof gated *appearance* is handled by component-require-variant.css,
 * keyed off <product-info data-require-selection data-selection-pending>. This script
 * formalises the logical state on load (uncheck inputs, blank the variant id) and removes
 * the pending flag on the first real selection — at which point the theme's native
 * variant-change flow (product-info.js → toggleSubmitButton) restores the standard
 * "Ajouter au panier" label.
 *
 * THE BUTTON IS NEVER DISABLED WHILE PENDING. Merchant decision, call of 2026-08-05
 * (Olivier Gaudéchoux): a shopper who cannot tell why nothing happens is the actual
 * problem — "que la personne puisse cliquer dessus et que ça lui mette pourquoi ça ne
 * fonctionne pas plutôt que juste un truc grisé". So the button stays live, reads
 * «Choisir une taille», and PRESSING IT SURFACES THE ERROR «Merci de bien choisir une
 * taille» in the form's own error slot — right at the button, not at the top of the page
 * (the explicit improvement over the reference site shown on that call, where the message
 * appeared away from the button and shoppers missed it).
 *
 * Interception is bound on <product-info> in the CAPTURE phase, deliberately: product-form.js
 * binds its submit handler on the <form> itself, and at the target listeners run in
 * registration order regardless of capture flag — so a listener on the form could not be
 * guaranteed to run first. An ancestor capture listener always does.
 *
 * Coexists with enforcement.js: that script only re-enables a button it itself blocked
 * (data-enforcement-blocked), so it never fights this gate.
 */

(function () {
  function gate(productInfo) {
    // Clear the server-rendered "first available" selection on the PDP pill picker.
    productInfo
      .querySelectorAll('variant-selects[data-primary] input[type="radio"]')
      .forEach(function (input) {
        input.checked = false;
      });

    // Reset the sticky-bar <select> to a non-selecting placeholder.
    productInfo
      .querySelectorAll('variant-selects[data-sticky] select')
      .forEach(function (select) {
        if (select.querySelector('option[data-require-placeholder]')) return;
        // Strip the server-rendered selected attribute so VariantSelects.selectedOptionValues
        // (which reads option[selected]) does not emit a stale value alongside the user's pick.
        select.querySelectorAll('option[selected]').forEach(function (option) {
          option.removeAttribute('selected');
        });
        var placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.disabled = true;
        placeholder.selected = true;
        placeholder.setAttribute('data-require-placeholder', '');
        placeholder.textContent =
          (window.theme && theme.variantStrings && theme.variantStrings.selectVariant) || '';
        select.insertBefore(placeholder, select.firstChild);
        select.value = '';
      });

    // Blank the hidden variant id on every form so an express/programmatic submit
    // cannot add the first variant before a size is chosen.
    productInfo.querySelectorAll('input[name="id"]').forEach(function (input) {
      input.value = '';
    });

    // NO disabled attribute here — see the header note. The blanked variant id above is
    // what actually prevents an add; the button stays pressable so the shopper gets told why.
  }

  // Show/hide the "choose an option" error in a form's own error slot. product-form.js owns that
  // slot (handleErrorMessage), so reuse it rather than inventing a second error UI — it already
  // carries role="alert" and the red icon.
  //
  // EXCEPT where the form has data-hide-errors, which buy-buttons sets while the gift-card
  // recipient feature is active. handleErrorMessage then returns immediately, and the effect was
  // that the GIFT CARD gate was completely SILENT: pressing add-to-cart with no amount chosen
  // blocked the add and said nothing at all (found 2026-08-16). The one product whose option is
  // not a size was also the one product that never explained itself.
  //
  // The opt-out is kept, not removed — it exists so Shopify's own recipient-form errors stay in
  // the recipient form. It simply should not swallow OUR error, which is about choosing an option
  // and has nothing to do with the recipient fields. Where it applies we write the same slot
  // directly, mirroring exactly what handleErrorMessage does.
  function setError(productForm, message) {
    if (!productForm) return;

    if (typeof productForm.handleErrorMessage === 'function' && !productForm.hideErrors) {
      productForm.handleErrorMessage(message || false);
      return;
    }

    var wrapper = productForm.querySelector('.product-form__error-message-wrapper');
    if (!wrapper) return;
    wrapper.toggleAttribute('hidden', !message);
    var slot = wrapper.querySelector('.product-form__error-message');
    if (message && slot) slot.textContent = message;
  }

  function clearErrors(productInfo) {
    productInfo.querySelectorAll('product-form').forEach(function (productForm) {
      setError(productForm, false);
    });
  }

  function initSection(productInfo) {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    gate(productInfo);

    // Capture-phase, on the ancestor: runs before product-form.js's own form handler, so a
    // pending submit never reaches the add-to-cart request. Bound once and left in place —
    // it self-checks the pending flag, so it goes inert the moment a size is chosen.
    productInfo.addEventListener(
      'submit',
      function (event) {
        if (!productInfo.hasAttribute('data-selection-pending')) return;

        var form = event.target;
        if (!form || form.tagName !== 'FORM') return;

        event.preventDefault();
        event.stopPropagation();

        setError(
          form.closest('product-form'),
          (window.theme && theme.variantStrings && theme.variantStrings.selectVariantError) || ''
        );
      },
      true
    );

    var unsubscribe = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, function (payload) {
      var target = payload && payload.data && payload.data.event && payload.data.event.target;
      if (!target || !productInfo.contains(target)) return;

      // A real selection has been made — release the gate. The native flow takes over
      // (re-renders the picker with the selection and toggles the submit button).
      productInfo.removeAttribute('data-selection-pending');
      // The reason for the error is gone, so the error goes with it.
      clearErrors(productInfo);
      if (typeof unsubscribe === 'function') unsubscribe();
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    document
      .querySelectorAll('product-info[data-require-selection][data-selection-pending]')
      .forEach(initSection);
  });
})();
